import * as format from '../lib/axel/format';
import * as localePresets from '../lib/axel/format/locale-presets';
import { AxelDate } from '../lib/axel/type';
import { getTextFormatByCurrencyPreset } from '@mode/shared/util-js';
import { ColumnSettings } from '@mode/legacy/data-access-model-js';
import ColumnOptionsService from './column-options-service';
import { ChartTypes } from '@mode/shared/contract-common';

class FormatOptionsService {
  static $inject = ['ColumnOptionsService'];

  public constructor(private cos: ColumnOptionsService) {}

  /**
   * Column Locale Settings
   *
   * Compose locale settings from user defined column settings
   * @param config
   * @returns {any}
   */
  public columnLocaleSettings(config) {
    const confParser = FormatOptionsService.columnTypeSettingsToLocaleOptions;

    return this.getLocalePresets(['enUS'], confParser(config));
  }

  static columnTypeSettingsToLocaleOptions(settings) {
    let options;

    if (JSON.stringify(settings) !== '{}') {
      options = {
        decimal: settings.decimalStyle,
        thousands: settings.thousandSeparator,
        grouping: [settings.thousandGroup],
      };
      if (settings.symbolPlacement) {
        options['currency'] = settings.symbolPlacement === 'after' ? ['', settings.symbol] : [settings.symbol, ''];
      }
    } else {
      // TODO consider moving this to the locale-presets as default
      options = { thousands: '' };
    }

    return options;
  }

  static textFormatToLocaleOptions(textFormat: ChartTypes.TextFormat) {
    const options = {
      decimal: textFormat.decimalStyle || '.',
      thousands: textFormat.separator != null ? textFormat.separator : ',',
      grouping: [textFormat.grouping != null ? textFormat.grouping : 3],
    };

    if (textFormat.currencyPreset != null && textFormat.currencyPreset !== ChartTypes.CurrencyPreset.Custom) {
      textFormat = {
        ...textFormat,
        ...getTextFormatByCurrencyPreset(textFormat.currencyPreset),
      };
    }

    if (textFormat.currencySymbolPrefix != null || textFormat.currencySymbolSuffix != null) {
      options['currency'] = [textFormat.currencySymbolPrefix || '', textFormat.currencySymbolSuffix || ''];
    } else if (textFormat.type === ChartTypes.FormatType.Financial) {
      options['currency'] = ['$', '']; // defaulting to $ for now
    }

    return options;
  }

  public getLocalePresets(query: string[], opts?) {
    if (opts) {
      return localePresets.get(query, opts);
    } else {
      return localePresets.get(query);
    }
  }

  /**
   * Get Preset Formatters
   *
   * Reduce non custom preset formatters from column keys, column type and global values
   * @param tableToken
   * @returns {{}}
   */
  public getPresetFormatters(columnSettings: ColumnSettings = {}) {
    const formatters = {};
    const columnKeys = Object.keys(columnSettings || {});
    const isPresetCurrency = (colKey) => {
      const type = this.cos.columnType(columnSettings, colKey);
      const typeVariant = this.cos.columnTypeVariant(columnSettings, colKey);
      const variantDefined = 'string' === typeof typeVariant;
      const notCustom = typeVariant !== 'custom';
      const isCurrency = type === 'financial' || type === 'currency';

      return variantDefined && notCustom && isCurrency;
    };

    return columnKeys
      .filter((key) => isPresetCurrency(key))
      .reduce(this.presetCurrencyReducer(columnSettings), formatters);
  }

  public getFormatter(columnSettings: ColumnSettings, colKey: string, data, colType: any | string, currencyFormatters) {
    const DEFAULT_FORMATTER_NAME = 'plain-text';
    const columnTypeVariant = this.cos.columnTypeVariant(columnSettings, colKey);
    const formatters = {
      'plain-text': () => format.string(),
      automatic: () => (data instanceof AxelDate ? formatters['date']() : formatters['plain-text']()),
      date: () => this.customDateFormatter(columnSettings, colKey),
      number: () => this.customFormatter(columnSettings, colKey, colType),
      financial: () => currencyFormatters[colKey],
      currency: () => formatters['financial'](),
      percentage: () => formatters['number'](),
      custom: {
        financial: () => formatters['number'](),
        currency: () => formatters['number'](),
      },
    };
    const defaultFormatter = formatters[DEFAULT_FORMATTER_NAME];
    const formatter = columnTypeVariant !== 'custom' ? formatters[colType] : formatters['custom'][colType];

    return (formatter || defaultFormatter)();
  }

  /**
   * Preset Currency Reducer
   *
   * @param tableToken
   * @returns {function(any, any): any}
   */
  private presetCurrencyReducer(columnSettings: ColumnSettings) {
    /**
     * Insert Formatter
     *
     * @param formatters
     * @param colKey
     * @returns {any}
     */
    const insertFormatter = (formatters, colKey) => {
      this.pushFormatter(formatters, colKey, this.getCurrencyFormatter(columnSettings, colKey));
      return formatters;
    };
    return insertFormatter;
  }

  /**
   * Push Formatter
   *
   * Attempt to push formatter method in a formatter collection
   * @param formatters
   * @param key
   * @param formatterFn
   * @returns {any}
   */
  private pushFormatter(formatters, key, formatterFn) {
    if (!formatterFn) {
      throw new Error('Invalid Formatter method');
    }
    if (formatters[key]) {
      throw new Error('Cannot override existing formatter');
    }
    formatters[key] = formatterFn;
    return formatters;
  }

  /**
   * Get Currency Formatter
   *
   * Creates currency format method after retrieving configurations based on
   * type variant (eg. 'usd' currency type)
   * @todo refactor needed there are redundant conditionals for financial vs currency codepaths
   * @param tableToken
   * @param colKey
   * @returns {any}
   */
  private getCurrencyFormatter(columnSettings: ColumnSettings, colKey: string) {
    const type = this.cos.columnType(columnSettings, colKey);
    const typeVariant = this.cos.columnTypeVariant(columnSettings, colKey);
    const config = this.getCurrencyFormatterConfig(typeVariant);

    if (type === 'financial') {
      return format.financial(config);
    } else {
      return format.currency(config);
    }
  }

  /**
   * Get Currency Formatter Config
   *
   * Retrieve currency locale settings by querying local presets with an
   * array of locale names.
   * @todo factor out the locale currency name lookup into another method
   * @see {localePresets}
   * @param currencyName
   * @returns {any}
   */
  private getCurrencyFormatterConfig(currencyName: string) {
    const localeQuery = ['enUS'];
    const localeLookup = localePresets.CURRENCY_LOCALE_MAP;
    const getLocaleNameFromCurrencyName = (name) => localeLookup[name];

    if (!localeLookup.hasOwnProperty(currencyName)) {
      throw new Error('Unknown currency type variant provided');
    }

    // update query
    localeQuery.push(getLocaleNameFromCurrencyName(currencyName));
    // create formatter
    return localePresets.get(localeQuery);
  }

  /**
   * Custom Formatter
   *
   * Compose formatter from user input and locale (column settings)
   * @param tableToken
   * @param colKey
   * @param colType
   * @returns {any}
   */
  public customFormatter(columnSettings: ColumnSettings, colKey: string, colType) {
    const options = {};
    const userSettings = this.cos.columnTypeSettings(columnSettings, colKey);
    const localeSettings = this.columnLocaleSettings(userSettings || {});

    if (userSettings && !isNaN(parseInt(userSettings.decimalPlaces))) {
      options['precision'] = parseInt(userSettings.decimalPlaces);
    }

    if (colType === 'financial') {
      return format.financial(localeSettings, options);
    } else if (colType === 'currency') {
      return format.currency(localeSettings, options);
    } else if (colType === 'number') {
      return format.number(localeSettings, options);
    } else if (colType === 'percentage') {
      return format.percentage(localeSettings, options);
    }
  }

  public customDateFormatter(columnSettings: ColumnSettings, colKey) {
    const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
    const preset = this.cos.columnTypeSetting(columnSettings, colKey, 'preset');
    const formatMap = {
      'yyyy-MM-dd': 'yyyy-MM-dd',
      'yyyy-MM-dd HH:mm:ss': 'yyyy-MM-dd HH:mm:ss',
      'MMM yyyy': 'MMM yyyy',
      'd MMM yyyy': 'd MMM yyyy',
      'MMM d yyyy': 'MMM d yyyy',
      none: '',
    };
    const resolveFormatType = (type) => {
      if (formatMap.hasOwnProperty(type)) {
        return type;
      } else {
        return DEFAULT_DATE_FORMAT;
      }
    };

    return format.date(formatMap[resolveFormatType(preset)]);
  }
}

export default FormatOptionsService;
